import PageHeader from "@/components/pageHeader";
import { Play, ChevronLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const HumansOfGillcoPage = () => {
  const humans = [
    {
      name: "Ms. Kulpreet Brar",
      brief: "Kulpreet Brar is a fierce woman who has redefined the meaning of hard work & dedication. She symbolises strong women who are born to achieve & conquer.",
      image: "/assets/culture/KulpreetBrar.png",
      videoId: "uGQFik_Yokg",
    },
    {
      name: "Col. SPS Gill",
      brief: "Col. SPS Gill is a valourous soldier who has served the country with pride all his life. His courageous story and all-positive view of life are worth watching every second.",
      image: "/assets/culture/SPSGill.png",
      videoId: "uXjQ_tmxgZQ",
    },
    {
      name: "Ms. Sangeeta Gupta",
      brief: "Turning her weakness into her strength, Sangeeta Gupta is a mother who became her family's cornerstone through their journey.",
      image: "/assets/culture/SangeetaGupta.png",
      videoId: "d4N2_fZR-WI",
    },
    {
      name: "Mr. Jagbir Maangat",
      brief: "Jagbir Maangat is an entrepreneur and believer who fought against all odds to entrench a successful life and career for himself.",
      image: "/assets/culture/JagbirMaangat.png",
      videoId: "NMVc9gBlWSU",
    },
    {
      name: "Brig. Inderjeet Singh",
      brief: "Witness Brig. Inderjeet Singh's inspiring journey of courage and resilience, from serving in the 1971 war to redefining life beyond the army.",
      image: "/assets/culture/InderjeetSingh.png",
      videoId: "DP9NVfoNZew",
    },
    {
      name: "Ms. Dheeraj",
      brief: "Ms. Dheeraj's journey from adversity to recovery reflects extraordinary resilience, inspiring hope and strength in those facing life's toughest struggles.",
      image: "/assets/culture/Dheeraj.png",
      videoId: "CznSOZ023jk",
    },
  ];

  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <div data-testid="v13-humans-page">
      <PageHeader title="Humans of Gillco" subtitle="Stories That Inspire" backgroundImage="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1920" />

      {/* Editorial hero — matches Awards / About / Vision style */}
      <section className="bg-white pt-24 lg:pt-32 pb-16 lg:pb-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="flex gap-8 lg:gap-16">
            <div className="hidden md:flex flex-col items-center">
              <span
                className="text-[11px] tracking-[0.3em] uppercase text-[#C4A265]"
                style={{ writingMode: "vertical-lr", letterSpacing: "0.3em" }}
              >
                OUR STORIES
              </span>
              <div className="w-[1px] bg-[#C4A265] flex-1 mt-4 min-h-[120px]" />
            </div>
            <div className="flex-1">
              <h2
                className="text-4xl sm:text-5xl lg:text-6xl text-[#0A0A0A] mb-10 lg:mb-12 leading-[1.05] relative inline-block pr-6"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Stories of<br />Courage, Character & Inspiration
                <span className="absolute bottom-3 -right-2 w-2 h-2 rounded-full bg-[#C4A265]" />
              </h2>
              <p className="text-[#4A4A4A] leading-relaxed max-w-3xl" style={{ textAlign: "justify" }}>
                Beyond the walls and skylines, Gillco is built of people &mdash; resilient, inspiring, and remarkable. Their stories are the heart of who we are. From war veterans and entrepreneurs to mothers, fighters and dreamers, each voice here reflects the courage and character of the communities we serve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Blocks - Alternating Editorial */}
      <HumansOptionC humans={humans} onPlay={setActiveVideo} />

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4 py-8 animate-[fadeIn_0.3s_ease-out]"
          onClick={() => setActiveVideo(null)}
          data-testid="v13-humans-video-modal"
        >
          <button
            type="button"
            onClick={() => setActiveVideo(null)}
            className="absolute top-6 right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Close video"
          >
            <ChevronLeft size={24} className="rotate-45" />
          </button>
          <div
            className="relative w-full max-w-5xl aspect-video bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&rel=0`}
              title="Humans of Gillco"
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </div>
  );
};

// ============ Alternating Editorial Story Blocks (compact portrait) ============
const HumanPortrait = ({ human, className = "" }) => (
  human.image ? (
    <img src={human.image} alt={human.name} className={`w-full h-full object-cover ${className}`} />
  ) : (
    <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br from-[#f8f6f2] to-[#ece6d8] ${className}`}>
      <span className="text-6xl text-[#C4A265]/60 font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {human.name.split(" ").slice(-1)[0][0]}
      </span>
    </div>
  )
);


const HumansStoryRow = ({ h, i, reversed, onPlay }) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.unobserve(el); } }, { threshold: 0.25 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`relative grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${reversed ? "lg:[direction:rtl]" : ""}`}
      data-testid={`humans-story-${i}`}
    >
      {/* Gold dot on timeline */}
      <div
        className="hidden lg:block absolute left-1/2 top-1/2 w-3 h-3 rounded-full bg-[#C4A265] ring-4 ring-white z-10"
        style={{
          transform: `translate(-50%, -50%) scale(${vis ? 1 : 0})`,
          opacity: vis ? 1 : 0,
          transition: "all 0.7s cubic-bezier(0.16,1,0.3,1) 0.3s",
        }}
      />

      {/* Portrait — compact, framed, elegant */}
      <div className="lg:col-span-5 [direction:ltr]">
        <div className={`relative max-w-[340px] mx-auto lg:mx-0 ${reversed ? "lg:ml-auto" : ""}`}>
          {/* Cream backdrop offset panel — slides into place from behind */}
          <div
            className="absolute inset-0 bg-[#f5f0e3] -z-10 hidden lg:block"
            style={{
              transform: vis
                ? (reversed ? "translate(1rem, 1rem)" : "translate(-1rem, 1rem)")
                : "translate(0, 0)",
              opacity: vis ? 1 : 0,
              transition: "transform 1s cubic-bezier(0.16,1,0.3,1) 0.25s, opacity 0.8s ease 0.25s",
            }}
          />
          {/* Gold corner accents on the frame */}
          <div className="absolute -top-2 -left-2 w-6 h-[2px] bg-[#C4A265] z-20" style={{ opacity: vis ? 1 : 0, transition: "opacity 0.6s ease 0.55s" }} />
          <div className="absolute -top-2 -left-2 w-[2px] h-6 bg-[#C4A265] z-20" style={{ opacity: vis ? 1 : 0, transition: "opacity 0.6s ease 0.65s" }} />
          <div className="absolute -bottom-2 -right-2 w-6 h-[2px] bg-[#C4A265] z-20" style={{ opacity: vis ? 1 : 0, transition: "opacity 0.6s ease 0.55s" }} />
          <div className="absolute -bottom-2 -right-2 w-[2px] h-6 bg-[#C4A265] z-20" style={{ opacity: vis ? 1 : 0, transition: "opacity 0.6s ease 0.65s" }} />

          <button
            type="button"
            onClick={() => onPlay(h.videoId)}
            className="group relative block w-full aspect-[4/5] overflow-hidden bg-[#f8f6f2]"
            aria-label={`Play story of ${h.name}`}
            data-testid={`humans-portrait-${i}`}
            style={{
              opacity: vis ? 1 : 0,
              transform: vis ? "translateY(0)" : "translateY(24px)",
              transition: "opacity 0.9s cubic-bezier(0.16,1,0.3,1), transform 0.9s cubic-bezier(0.16,1,0.3,1)",
            }}
          >
            <HumanPortrait human={h} className="group-hover:scale-[1.04] transition-transform duration-1000" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-14 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 group-hover:bg-white transition-all duration-500 shadow-xl">
                <Play size={18} className="text-[#0A4F3B] ml-1" fill="#0A4F3B" />
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Story text */}
      <div
        className="lg:col-span-7 [direction:ltr]"
        style={{
          opacity: vis ? 1 : 0,
          transform: vis ? "translateX(0)" : `translateX(${reversed ? "-30px" : "30px"})`,
          transition: "opacity 0.9s cubic-bezier(0.16,1,0.3,1) 0.2s, transform 0.9s cubic-bezier(0.16,1,0.3,1) 0.2s",
        }}
      >
        <span className="text-[10px] uppercase tracking-[0.4em] text-[#C4A265] block mb-4 font-medium">
          In Their Words &nbsp;·&nbsp; Story {String(i + 1).padStart(2, "0")}
        </span>
        <h4 className="text-3xl lg:text-5xl text-[#0A0A0A] font-light leading-[1.05] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {h.name}
        </h4>
        <div
          className="h-[2px] bg-[#C4A265] mb-6"
          style={{ width: vis ? "3.5rem" : "0", transition: "width 0.8s cubic-bezier(0.16,1,0.3,1) 0.45s" }}
        />
        <p className="text-[15px] text-[#4A4A4A] leading-[1.85] mb-7 max-w-xl" style={{ textAlign: "justify" }}>
          {h.brief}
        </p>
        <button
          type="button"
          onClick={() => onPlay(h.videoId)}
          className="inline-flex items-center gap-3 bg-[#0A4F3B] text-white tracking-[0.2em] uppercase text-xs px-8 py-4 hover:bg-[#0d6b4f] transition-all duration-300"
          data-testid={`humans-cta-${i}`}
        >
          <Play size={14} fill="#fff" /> Watch the Story
        </button>
      </div>
    </div>
  );
};

const HumansOptionC = ({ humans, onPlay }) => {
  return (
    <section className="bg-white pb-24 lg:pb-32" data-testid="humans-stories-section">
      <div className="relative max-w-6xl mx-auto px-6 lg:px-12">
        {/* Vertical gold timeline */}
        <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-[#C4A265]/40 to-transparent" />
        <div className="space-y-24 lg:space-y-32">
          {humans.map((h, i) => (
            <HumansStoryRow key={i} h={h} i={i} reversed={i % 2 === 1} onPlay={onPlay} />
          ))}
        </div>
      </div>
    </section>
  );
};